<template>
    <span class="product-area-label">
        <template
            v-for="(area, index) in shownProductAreas"
            :key="`${area.id ?? index}`">
            <pendo-divider
                v-if="index > 0"
                direction="vertical"
                decorative
                :stroke-width="2"
                width="16px"
                height="14px" />
            <icon-label
                :show-icon="showIcon"
                :show-label="showLabel"
                :size="size"
                :icon="{ type: 'square', fill: GROUP_COLORS.colorMap[area.color], strokeWidth: '0' }"
                :label="area.name" />
        </template>
        <template v-if="additionalProductAreas.length > 0">
            <pendo-popover-v2
                arrow
                placement="bottom">
                <span class="product-area-label__additional">+{{ additionalProductAreas.length }}</span>
                <template #popover>
                    <template
                        v-for="(area, index) in additionalProductAreas"
                        :key="`additional-product-area-${area.id ?? index}`">
                        <icon-label
                            :show-icon="showIcon"
                            :show-label="showLabel"
                            :size="size"
                            :icon="{ type: 'square', fill: GROUP_COLORS.colorMap[area.color], strokeWidth: '0' }"
                            :label="area.name" />
                    </template>
                </template>
            </pendo-popover-v2>
        </template>
    </span>
</template>

<script setup>
import IconLabel from '@/common/icon-label/icon-label.vue';
import { GROUP_COLORS } from '@/constants';
import { formatProductAreas } from '@/voc/helpers/common.helper';
import { PendoDivider, PendoPopoverV2 } from '@pendo/components';
import { computed } from 'vue';

const props = defineProps({
    /** Single id (string), array of ids, or resolved object(s) with id, name, color */
    productArea: {
        type: [String, Array, Object],
        required: true,
    },
    size: {
        type: String,
        default: 'medium',
        validator: (value) => ['small', 'medium', 'large', 'xlarge'].includes(value),
    },
    showLabel: {
        type: Boolean,
        default: true,
    },
    showIcon: {
        type: Boolean,
        default: false,
    },
    showMax: {
        type: [Number, String],
        default: 1,
    },
});

const resolvedProductAreas = computed(() => {
    const raw = props.productArea;
    if (raw == null) return [];
    if (typeof raw === 'string' || Array.isArray(raw)) {
        return formatProductAreas(raw);
    }

    return [raw];
});

const shownProductAreas = computed(() => {
    return resolvedProductAreas.value.slice(0, props.showMax);
});

const additionalProductAreas = computed(() => {
    return resolvedProductAreas.value.slice(props.showMax);
});
</script>

<style scoped>
.product-area-label {
    display: flex;
    flex-wrap:nowrap;
    gap: 0.5rem;
    overflow: hidden;
    align-items: center;
}
.product-area-label__additional {
    color: var(--palette-gray-70);
    cursor: pointer;
}
</style>
